import React, { useEffect, useState, useMemo, useContext } from "react";
import API from "../api";
import { Link } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { motion } from "framer-motion";

import {
  Users,
  TrendingUp,
  CheckCircle,
  IndianRupee,
  Plus,
  Search,
  Filter,
  ArrowLeft,
  ChevronRight,
  Loader2,
  Building2,
  RotateCcw,
} from "lucide-react";

const ClientSection = () => {
  const { orgSlug, loading: authLoading } =
    useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);

  // ================= FILTER STATES =================
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [industryFilter, setIndustryFilter] =
    useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [districtFilter, setDistrictFilter] =
    useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [countryFilter, setCountryFilter] =
    useState("all");

  const [sortBy, setSortBy] = useState("newest");

  // ================= FETCH CLIENTS =================
  const fetchClients = async () => {
    try {
      setLoading(true);

      const res = await API.get("clients/");

      setClients(res.data.clients || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && orgSlug) {
      fetchClients();
    }
  }, [authLoading, orgSlug]);

  // ================= FILTER CLIENTS =================
  const filteredClients = useMemo(() => {
    let filtered = [...clients];

    // SEARCH
    filtered = filtered.filter((client) => {
      const searchText = search.toLowerCase();

      return (
        client.name
          ?.toLowerCase()
          .includes(searchText) ||
        client.company
          ?.toLowerCase()
          .includes(searchText) ||
        client.city
          ?.toLowerCase()
          .includes(searchText) ||
        client.industry
          ?.toLowerCase()
          .includes(searchText)
      );
    });

    // STATUS
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (c) =>
          c.status?.trim().toLowerCase() ===
          statusFilter.trim().toLowerCase()
      );
    }

    // INDUSTRY
    if (industryFilter !== "all") {
      filtered = filtered.filter(
        (c) =>
          c.industry?.trim().toLowerCase() ===
          industryFilter.trim().toLowerCase()
      );
    }

    // CITY
    if (cityFilter !== "all") {
      filtered = filtered.filter(
        (c) =>
          c.city?.trim().toLowerCase() ===
          cityFilter.trim().toLowerCase()
      );
    }

    // DISTRICT
    if (districtFilter !== "all") {
      filtered = filtered.filter(
        (c) =>
          c.district?.trim().toLowerCase() ===
          districtFilter.trim().toLowerCase()
      );
    }

    // STATE
    if (stateFilter !== "all") {
      filtered = filtered.filter(
        (c) =>
          c.state?.trim().toLowerCase() ===
          stateFilter.trim().toLowerCase()
      );
    }

    // COUNTRY
    if (countryFilter !== "all") {
      filtered = filtered.filter(
        (c) =>
          c.country?.trim().toLowerCase() ===
          countryFilter.trim().toLowerCase()
      );
    }

    // SORTING
    if (sortBy === "revenue") {
      filtered.sort(
        (a, b) => (b.revenue || 0) - (a.revenue || 0)
      );
    } else if (sortBy === "name") {
      filtered.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      );
    } else {
      filtered.sort((a, b) => {
        const dateA = a.createdAt?.seconds
          ? new Date(a.createdAt.seconds * 1000)
          : new Date(a.createdAt || 0);

        const dateB = b.createdAt?.seconds
          ? new Date(b.createdAt.seconds * 1000)
          : new Date(b.createdAt || 0);

        return dateB - dateA;
      });
    }

    return filtered;
  }, [
    clients,
    search,
    statusFilter,
    industryFilter,
    cityFilter,
    districtFilter,
    stateFilter,
    countryFilter,
    sortBy,
  ]);

  // ================= LOADING =================
  if (authLoading || !orgSlug) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2
          className="animate-spin text-indigo-600"
          size={38}
        />
      </div>
    );
  }

  // ================= KPI DATA =================
  const totalRevenue = clients.reduce(
    (sum, c) => sum + (c.revenue || 0),
    0
  );

  const totalLeads = clients.filter(
    (c) =>
      c.status?.trim().toLowerCase() === "lead"
  ).length;

  const totalContacts = clients.filter(
    (c) =>
      c.status?.trim().toLowerCase() === "contact"
  ).length;

  const totalClients = clients.filter(
    (c) =>
      c.status?.trim().toLowerCase() === "client"
  ).length;

  // ================= FILTER OPTIONS =================
  const industries = [
    ...new Set(
      clients
        .map((c) => c.industry?.trim())
        .filter(Boolean)
    ),
  ].sort();

  const cities = [
    ...new Set(
      clients.map((c) => c.city?.trim()).filter(Boolean)
    ),
  ].sort();

  const districts = [
    ...new Set(
      clients
        .map((c) => c.district?.trim())
        .filter(Boolean)
    ),
  ].sort();

  const states = [
    ...new Set(
      clients.map((c) => c.state?.trim()).filter(Boolean)
    ),
  ].sort();

  const countries = [
    ...new Set(
      clients
        .map((c) => c.country?.trim())
        .filter(Boolean)
    ),
  ].sort();

  // ================= CLEAR FILTERS =================
  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setIndustryFilter("all");
    setCityFilter("all");
    setDistrictFilter("all");
    setStateFilter("all");
    setCountryFilter("all");
    setSortBy("newest");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-6">

      {/* ================= HEADER ================= */}
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row justify-between items-start lg:items-center gap-5 mb-7">

        <div>
          <Link
            to={`/${orgSlug}/admin-dashboard`}
            className="group flex items-center text-slate-500 hover:text-indigo-600 text-sm font-semibold transition-all mb-2"
          >
            <ArrowLeft
              size={15}
              className="mr-2 group-hover:-translate-x-1 transition-transform"
            />
            Admin Dashboard
          </Link>

          <h1 className="text-3xl md:text-4xl font-black tracking-tight text-slate-900">
            Client Management
          </h1>

          <p className="text-slate-500 mt-1 text-sm font-medium">
            Monitoring {clients.length} business
            partnerships
          </p>
        </div>

        <Link
          to={`/${orgSlug}/clients/add`}
          className="
            flex items-center gap-2
            bg-indigo-600 hover:bg-indigo-700
            text-white
            px-5 py-3
            rounded-2xl
            text-sm font-bold
            shadow-lg shadow-indigo-100
            transition-all
            active:scale-95
          "
        >
          <Plus size={16} />
          Add Client
        </Link>
      </div>

      {/* ================= KPI GRID ================= */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">

        <KpiCard
          title="Total Leads"
          value={totalLeads}
          icon={<Users className="text-amber-600" />}
          color="bg-amber-50"
        />

        <KpiCard
          title="Contacts"
          value={totalContacts}
          icon={
            <TrendingUp className="text-blue-600" />
          }
          color="bg-blue-50"
        />

        <KpiCard
          title="Clients"
          value={totalClients}
          icon={
            <CheckCircle className="text-emerald-600" />
          }
          color="bg-emerald-50"
        />

        <KpiCard
          title="Revenue"
          value={`₹${totalRevenue.toLocaleString(
            "en-IN"
          )}`}
          icon={
            <IndianRupee className="text-purple-600" />
          }
          color="bg-purple-50"
        />
      </div>

      {/* ================= FILTER SECTION ================= */}
      <div className="max-w-7xl mx-auto mb-6">

        <div className="bg-white border border-slate-200 rounded-[1.7rem] shadow-sm px-5 py-4">

          {/* TOP BAR */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">

            {/* SEARCH */}
            <div className="relative w-full xl:max-w-sm">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                type="text"
                placeholder="Search clients..."
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
                className="
                  w-full
                  h-10
                  pl-9 pr-4
                  rounded-xl
                  border border-slate-200
                  bg-slate-50
                  text-[13px]
                  font-medium
                  text-slate-700
                  placeholder:text-slate-400
                  focus:outline-none
                  focus:ring-4 focus:ring-indigo-100
                  focus:border-indigo-400
                  transition-all
                "
              />
            </div>

            {/* RIGHT ACTIONS */}
            <div className="flex items-center gap-2 flex-wrap">

              <div
                className="
                  h-10
                  px-4
                  rounded-xl
                  bg-indigo-50
                  text-indigo-700
                  text-[13px]
                  font-bold
                  flex items-center
                "
              >
                {filteredClients.length} Results
              </div>

              <button
                onClick={clearFilters}
                className="
                  h-10
                  px-4
                  rounded-xl
                  border border-slate-200
                  bg-slate-50
                  text-[13px]
                  font-semibold
                  text-slate-700
                  hover:bg-slate-100
                  transition-all
                  flex items-center gap-2
                "
              >
                <RotateCcw size={13} />
                Reset
              </button>
            </div>
          </div>

          {/* FILTER GRID */}
          <div
            className="
              mt-4
              grid
              grid-cols-2
              sm:grid-cols-3
              md:grid-cols-4
              lg:grid-cols-7
              gap-2
            "
          >

            {/* STATUS */}
            <div className="relative">
              <Filter
                size={13}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />

              <select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value)
                }
                className="professional-filter pl-8"
              >
                <option value="all">
                  All Status
                </option>

                <option value="lead">Lead</option>

                <option value="contact">
                  Contact
                </option>

                <option value="client">
                  Client
                </option>
              </select>
            </div>

            {/* INDUSTRY */}
            <select
              value={industryFilter}
              onChange={(e) =>
                setIndustryFilter(e.target.value)
              }
              className="professional-filter"
            >
              <option value="all">
                Industries
              </option>

              {industries.map((ind, i) => (
                <option key={i} value={ind}>
                  {ind}
                </option>
              ))}
            </select>

            {/* CITY */}
            <select
              value={cityFilter}
              onChange={(e) =>
                setCityFilter(e.target.value)
              }
              className="professional-filter"
            >
              <option value="all">Cities</option>

              {cities.map((city, i) => (
                <option key={i} value={city}>
                  {city}
                </option>
              ))}
            </select>

            {/* DISTRICT */}
            <select
              value={districtFilter}
              onChange={(e) =>
                setDistrictFilter(e.target.value)
              }
              className="professional-filter"
            >
              <option value="all">
                Districts
              </option>

              {districts.map((district, i) => (
                <option key={i} value={district}>
                  {district}
                </option>
              ))}
            </select>

            {/* STATE */}
            <select
              value={stateFilter}
              onChange={(e) =>
                setStateFilter(e.target.value)
              }
              className="professional-filter"
            >
              <option value="all">States</option>

              {states.map((state, i) => (
                <option key={i} value={state}>
                  {state}
                </option>
              ))}
            </select>

            {/* COUNTRY */}
            <select
              value={countryFilter}
              onChange={(e) =>
                setCountryFilter(e.target.value)
              }
              className="professional-filter"
            >
              <option value="all">
                Countries
              </option>

              {countries.map((country, i) => (
                <option key={i} value={country}>
                  {country}
                </option>
              ))}
            </select>

            {/* SORT */}
            <select
              value={sortBy}
              onChange={(e) =>
                setSortBy(e.target.value)
              }
              className="professional-filter"
            >
              <option value="newest">
                Newest First
              </option>

              <option value="revenue">
                By Revenue
              </option>

              <option value="name">
                Alphabetical
              </option>
            </select>
          </div>
        </div>
      </div>

      {/* ================= CLIENT TABLE ================= */}
      <div className="max-w-7xl mx-auto bg-white rounded-[1.8rem] shadow-sm border border-slate-100 overflow-hidden">

        {loading ? (
          <div className="p-16 text-center flex flex-col items-center">
            <Loader2
              className="animate-spin text-slate-300 mb-4"
              size={30}
            />

            <p className="text-slate-500 font-medium tracking-widest uppercase text-xs">
              Fetching Records
            </p>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="p-16 text-center">
            <Building2
              className="mx-auto text-slate-200 mb-4"
              size={44}
            />

            <p className="text-slate-500 font-bold">
              No clients match your criteria.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">

            <table className="w-full border-collapse">

              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="table-head">
                    Client Partner
                  </th>

                  <th className="table-head">
                    Sector
                  </th>

                  <th className="table-head">
                    Financials
                  </th>

                  <th className="table-head">
                    Status
                  </th>

                  <th className="table-head text-right">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-50">

                {filteredClients.map((client) => (
                  <motion.tr
                    key={client.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="group hover:bg-slate-50/70 transition-all"
                  >

                    <td className="p-4">
  <div className="flex items-center gap-3">

    <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white font-bold shadow-md shadow-indigo-100 text-sm">
      {client.name?.charAt(0).toUpperCase() || "C"}
    </div>

    <div>
      <div className="font-bold text-sm text-slate-900 group-hover:text-indigo-600 transition-colors">
        {client.name}
      </div>

      <div className="text-[11px] text-slate-400 font-medium">
        {client.company}
      </div>

      {/* Sales Rating */}
      <div className="flex items-center gap-1 mt-1">
        {[...Array(10)].map((_, i) => (
          <span
            key={i}
            className={`transition-all ${
              i < (client.salesStar || 1)
                ? "text-amber-400"
                : "text-slate-200"
            }`}
          >
            ★
          </span>
        ))}

        <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[10px] font-bold">
          {client.salesStar || 1}/10
        </span>
      </div>

      {/* Progress Bar */}
      <div className="w-32 h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-amber-400 to-yellow-500"
          style={{
            width: `${((client.salesStar || 1) / 10) * 100}%`,
          }}
        />
      </div>
    </div>

  </div>
</td>
            

                    {/* INDUSTRY */}
                    <td className="p-4">
                      <div className="text-sm font-bold text-slate-700">
                        {client.industry ||
                          "General"}
                      </div>
                    </td>

                    {/* REVENUE */}
                    <td className="p-4">
                      <div className="text-sm font-black text-slate-900">
                        ₹
                        {(
                          client.revenue || 0
                        ).toLocaleString("en-IN")}
                      </div>

                      <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-500 mt-1">
                        Contract Value
                      </div>
                    </td>

                    {/* STATUS */}
                    <td className="p-4">
                      <StatusBadge
                        status={client.status}
                      />
                    </td>

                    {/* ACTION */}
                    <td className="p-4 text-right">
                      <Link
                        to={`/${orgSlug}/clients/${client.id}`}
                        className="
                          inline-flex items-center gap-1
                          px-4 py-2
                          rounded-xl
                          border border-slate-200
                          bg-white
                          text-[11px]
                          font-black uppercase tracking-widest
                          text-slate-600
                          hover:bg-slate-900
                          hover:text-white
                          transition-all
                        "
                      >
                        Profile

                        <ChevronRight size={13} />
                      </Link>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/* ================= KPI CARD ================= */

const KpiCard = ({
  title,
  value,
  icon,
  color,
}) => (
  <div className="bg-white p-5 rounded-[1.7rem] shadow-sm border border-slate-100 hover:shadow-lg transition-all">

    <div
      className={`w-11 h-11 ${color} rounded-2xl flex items-center justify-center mb-4`}
    >
      {icon}
    </div>

    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
      {title}
    </p>

    <h2 className="text-2xl font-black text-slate-900 mt-1">
      {value}
    </h2>
  </div>
);

/* ================= STATUS BADGE ================= */

const StatusBadge = ({ status }) => {
  const config = {
    lead: {
      style:
        "bg-amber-50 text-amber-600 border-amber-100",
      label: "Lead",
    },

    contact: {
      style:
        "bg-blue-50 text-blue-600 border-blue-100",
      label: "Contact",
    },

    client: {
      style:
        "bg-emerald-50 text-emerald-600 border-emerald-100",
      label: "Client",
    },
  };

  const normalized = String(status || "")
    .trim()
    .toLowerCase();

  const current = config[normalized] || {
    style:
      "bg-slate-100 text-slate-600 border-slate-200",
    label: "Unknown",
  };

  return (
    <span
      className={`px-3 py-1 rounded-xl text-[10px] font-black uppercase tracking-widest border ${current.style}`}
    >
      {current.label}
    </span>
  );
};

export default ClientSection;